// D3-10 / D6-08 (FE-1.12, FE-3.3) — map raw chat/store errors to friendly,
// human copy. Raw error strings can contain internals ("connect ECONNREFUSED
// 10.0.0.7:5432", "504 Gateway Timeout", fetch stack noise) that must never be
// shown verbatim to end users. Unknown errors get a safe generic message; the
// raw value is still available to developers via the console (callers log it).
//
// Companion to the ErrorBoundary work from PR #21 (D1-09) and the auth-error
// mapper on batch/login-ux (D3-06): same guarantee — raw errors are never
// echoed into the UI.

export interface FriendlyError {
  /** Short, human headline. */
  title: string;
  /** One-sentence next step. */
  detail: string;
  /** True when retrying is likely to help (network/server blips). */
  retryable: boolean;
}

const NETWORK_PATTERNS = [
  /failed to fetch/i,
  /networkerror/i,
  /network request failed/i,
  /load failed/i,
  /econnrefused/i,
  /econnreset/i,
  /etimedout/i,
  /enotfound/i,
  /socket hang ?up/i,
  /fetch failed/i,
  /\b50[234]\b/,
  /gateway time-?out/i,
  /bad gateway/i,
  /service unavailable/i,
  /\boffline\b/i,
];

const AUTH_PATTERNS = [/\b401\b/, /\b403\b/, /unauthori[sz]ed/i, /forbidden/i, /session expired/i];

const RATE_LIMIT_PATTERNS = [/\b429\b/, /rate limit/i, /too many requests/i];

const SERVER_PATTERNS = [/\b500\b/, /internal server error/i];

function matchesAny(raw: string, patterns: RegExp[]): boolean {
  return patterns.some((pattern) => pattern.test(raw));
}

/**
 * Convert a raw error string (from the chat store, fetch failures, or server
 * responses) into user-safe copy. Never returns the raw input.
 */
export function friendlyChatError(raw: string | null | undefined): FriendlyError {
  const value = typeof raw === 'string' ? raw.trim() : '';

  if (matchesAny(value, NETWORK_PATTERNS)) {
    return {
      title: "We're having trouble reaching Ally.",
      detail: 'Check your connection and try again.',
      retryable: true,
    };
  }

  if (matchesAny(value, RATE_LIMIT_PATTERNS)) {
    return {
      title: 'Ally is a little busy right now.',
      detail: 'Give it a moment, then try again.',
      retryable: true,
    };
  }

  if (matchesAny(value, AUTH_PATTERNS)) {
    return {
      title: "You don't have access to that right now.",
      detail: 'Your session may have expired — try signing in again.',
      retryable: false,
    };
  }

  if (matchesAny(value, SERVER_PATTERNS)) {
    return {
      title: 'Ally ran into a problem on our side.',
      detail: 'Please try again in a moment.',
      retryable: true,
    };
  }

  // Client-generated store messages — keep intent, warm up the copy.
  if (/failed to send/i.test(value)) {
    return {
      title: "Your message didn't send.",
      detail: 'Check your connection and try again.',
      retryable: true,
    };
  }

  if (/failed to load (this )?conversation\b/i.test(value) || /failed to load messages/i.test(value)) {
    return {
      title: "We couldn't load this conversation.",
      detail: 'Please try again.',
      retryable: true,
    };
  }

  if (/failed to load conversations/i.test(value)) {
    return {
      title: "We couldn't load your conversations.",
      detail: 'Please try again.',
      retryable: true,
    };
  }

  if (/failed to load agents/i.test(value)) {
    return {
      title: "We couldn't load your agents.",
      detail: 'Please try again.',
      retryable: true,
    };
  }

  // Unknown error: safe default — never echo the raw string.
  return {
    title: 'Something went wrong.',
    detail: 'Please try again — if this keeps happening, contact IT.',
    retryable: true,
  };
}

/** Convenience single-line variant for compact surfaces (banners). */
export function friendlyChatErrorText(raw: string | null | undefined): string {
  const mapped = friendlyChatError(raw);
  return `${mapped.title} ${mapped.detail}`;
}
